import React, { useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import BaseSelect from 'components/shared/baseForm/BaseSelect';
import MarketplaceResidentBanner from 'components/shared/residentBanner/MarketplaceResidentBanner';
import MarketplaceMenuSection from 'components/marketplace/marketplaceBase/MarketplaceMenuSection';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { FormProvider, useForm } from 'react-hook-form';
import { useGetMarketplaceByFacilitySelectQuery, useGetMarketplaceByIdQuery, useAddMarketplaceOrderMutation, useAddMarketplaceOrderWithCardMutation } from 'store/apis/MarketplaceApi';
import { useGetResidentsAllQuery } from 'store/apis/ResidentsApi';
import { useOutletContext } from 'react-router-dom';

dayjs.extend(customParseFormat);

export default function MarketplaceResidents() {
  const currentUserData = useOutletContext();

  const { data: allResidents } = useGetResidentsAllQuery();

  const methods = useForm({ shouldUnregister: false, mode: "all" });
  const { watch, control, setValue } = methods;

  const selectedResidentId = watch("residentId");
  const marketplaceId = watch("marketplaceId");

  const selectedResident = useMemo(
    () => allResidents?.find((r) => r.id === selectedResidentId) ?? null,
    [allResidents, selectedResidentId]
  );

  const residentOptions = useMemo(
    () => allResidents?.map((r) => ({ id: r.id, label: r.name })) ?? [],
    [allResidents]
  );

  // guards against stale banner/menu rendering if residentId desyncs from the select's option list
  const isResidentValid = useMemo(
    () => !!selectedResidentId && residentOptions.some((r) => r.id === selectedResidentId),
    [selectedResidentId, residentOptions]
  );

  const { data: marketplaces } = useGetMarketplaceByFacilitySelectQuery(
    currentUserData?.facilityId,
    { skip: !currentUserData?.facilityId }
  );

  const [addMarketplaceOrder, { isLoading: isSubmitting }] = useAddMarketplaceOrderMutation();

  const [addMarketplaceOrderWithCard, { isLoading: isSubmittingWithCard }] = useAddMarketplaceOrderWithCardMutation();

  const { data: menuData, isFetching: menuDataLoading } = useGetMarketplaceByIdQuery(
    { id: marketplaceId, residentId: selectedResidentId },
    { skip: !marketplaceId || !selectedResidentId, refetchOnMountOrArgChange: 300 }
  );

  useEffect(() => {
    if (!marketplaceId) return;
    localStorage.setItem("lastMarketplaceId", marketplaceId);
  }, [marketplaceId]);

  useEffect(() => {
    if (!marketplaces?.length) return;
    const saved = localStorage.getItem("lastMarketplaceId");
    if (saved && marketplaces.some((m) => m.id == saved)) {
      setValue("marketplaceId", Number(saved));
    }
  }, [marketplaces]);

  const handleOrderSubmit = async (billingTypeId, selectedItems, orderTotal) => {
     if (billingTypeId === 1 && !name?.trim()) {
    const result = await addMarketplaceOrderWithCard({
      facilityId: currentUserData?.facilityId,
      bistroMenuId: marketplaceId,
      residentId: selectedResidentId,
      guestOfResidentId: null,
      employeeId: null,
      guestName: null,
      guestTypeId: null,
      price: orderTotal,
      billingTypeId,
      orderItems: Object.values(selectedItems).map((item) => ({
        bistroMenuItemId: item.id,
        itemName: item.name,
        category: item.bistroMenuCategoryName ?? "",
        subcategory: item.bistroMenuSubCategoryName ?? "",
        quantity: item.quantity,
        price: item.price ?? 0,
        note: item.note ?? "",
        subItems: Object.values(item.subItems || {}).map((sub) => ({
          bistroMenuSubItemId: sub.id,
          itemName: sub.name,
          quantity: sub.quantity,
          price: sub.price ?? 0,
        })),
      })),
    }).unwrap();
        console.log('***********',result);
    if (!result.error && result?.status === 'succeeded') {
      window.alert(
        `(SIMULATION) Payment successful!\nOrder: ${result.orderId}\nPayment: ${result.paymentIntentId}`
      );
      setValue("residentId", null, { shouldValidate: false, shouldDirty: true });
    } else {
      window.alert('(SIMULATION) Payment has not completed. Check its status before retrying.');
      return !result.error;
    }
    // if (!result.error) setValue("residentId", null, { shouldValidate: false, shouldDirty: true });
    // return !result.error;
   // window.location.href = result.checkoutUrl;
  }
  else{
    const result = await addMarketplaceOrder({
      facilityId: currentUserData?.facilityId,
      bistroMenuId: marketplaceId,
      residentId: selectedResidentId,
      guestOfResidentId: null,
      employeeId: null,
      guestName: null,
      guestTypeId: null,
      price: orderTotal,
      billingTypeId,
      orderItems: Object.values(selectedItems).map((item) => ({
        bistroMenuItemId: item.id,
        itemName: item.name,
        category: item.bistroMenuCategoryName ?? "",
        subcategory: item.bistroMenuSubCategoryName ?? "",
        quantity: item.quantity,
        price: item.price ?? 0,
        note: item.note ?? "",
        subItems: Object.values(item.subItems || {}).map((sub) => ({
          bistroMenuSubItemId: sub.id,
          itemName: sub.name,
          quantity: sub.quantity,
          price: sub.price ?? 0,
        })),
      })),
    });

    if (!result.error) setValue("residentId", null, { shouldValidate: false, shouldDirty: true });
    return !result.error;
  }
  };


  return (
    <div className="paperContent customScrollbar">
      <Box width="100%" padding="0 1rem">
        <FormProvider {...methods}>
          <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 4 }}>
              <BaseSelect
                name="residentId"
                id="residentId"
                label="Resident"
                control={control}
                options={residentOptions}
                sx={{ width: "100%" }}
              />
            </Grid>
            <Grid size={{ xs: 4 }} />
            <Grid size={{ xs: 4 }}>
              <BaseSelect
                name="marketplaceId"
                id="marketplaceId"
                label="Marketplace"
                control={control}
                options={marketplaces || []}
                sx={{ width: "100%" }}
              />
            </Grid>
              {isResidentValid && (
                <Grid size={{ xs: 12 }}>
                  <MarketplaceResidentBanner
                    residentId={selectedResidentId}
                    residentName={selectedResident?.name ?? ""}
                    showPhoto={selectedResident?.photoOnTrayCard ?? false}
                    roomNumber={selectedResident?.roomNumber}
                    bed={selectedResident?.bed}
                  />
                </Grid>
              )}
              {menuData?.isMenuAvailable && isResidentValid && marketplaceId ? (
                <MarketplaceMenuSection
                  menuData={menuData}
                  menuDataLoading={menuDataLoading}
                  isSubmitting={isSubmitting || isSubmittingWithCard}
                  showChargeResident={true}
                  onOrderSubmit={handleOrderSubmit}
                  marketplaceId={marketplaceId}
                />
              ) : (menuData && !menuData?.isMenuAvailable && isResidentValid) ? (
                <Grid size={{ xs: 12 }} width="100%">
                  <Typography
                    variant="h5"
                    textAlign="center"
                    gutterBottom
                    sx={{ whiteSpace: "pre-line"}} 
                  > 
                    {"This marketplace is not currently available."}{!!menuData?.bistroMenuSchedules?.length && "\nAvailability:"}
                  </Typography>
                  {!!menuData?.bistroMenuSchedules?.length && (
                    menuData.bistroMenuSchedules?.map((schedule) => {
                      return (
                        <Grid 
                          size={{ xs: 12 }} 
                          key={schedule.dayOfWeek} 
                          sx={{
                            display: "flex",
                            flexWrap: "nowrap"
                          }}
                        >
                          <Grid size={{ xs: 6 }} pr={2}>
                            <Typography 
                              textAlign="right" 
                              fontSize="1.25rem"                     
                            >
                              {dayjs().day(schedule.dayOfWeek).format("dddd")}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 6 }} pl={2}>
                            {schedule.isAvailable ? (
                              <>
                                <Typography
                                  textAlign="left" 
                                  fontSize="1.25rem"
                                >
                                  {dayjs(schedule.startTime, "HH:mm:ss").format('h:mm A')} - 
                                  {dayjs(schedule.endTime, "HH:mm:ss").format('h:mm A')}
                                </Typography>
                              </>
                            ) : (
                              <Typography
                                textAlign="left" 
                                fontSize="1.25rem"
                              >
                                Not Available
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      )
                    })
                  )}
                </Grid>
              ) : (
                <Grid size={{ xs: 12 }}>
                  <Typography
                    variant="h5"
                    textAlign="center"
                  >
                    {!marketplaceId && !isResidentValid ? "Please select a resident and marketplace"
                      : !marketplaceId ? "Please select a marketplace" : "Please select a resident"
                    }
                  </Typography>
                </Grid>
              )}
              </Grid>
        </FormProvider>
      </Box>
    </div>
  );
}